import { Link } from 'react-router-dom';
import { Zap, ChevronLeft, Wrench, Shield, Check } from 'lucide-react';

const StepHeader = ({ 
  title,
  subtitle,
  currentStep,
  steps,
  stepTitles,
  stepSubtitles,
  icon = '⚡',
  backLink = '/',
  backLabel = '← Back',
  variant = 'default'
}) => {
  const currentStepTitle = stepTitles?.[currentStep] || title;
  const currentStepSubtitle = stepSubtitles?.[currentStep] || subtitle;

  // Get icon component based on prop
  const getIcon = () => {
    if (icon === '⚡') return <Zap className="w-6 h-6 text-[#00F0FF]" />;
    if (icon === '🔧') return <Wrench className="w-6 h-6 text-[#00F0FF]" />;
    if (icon === '🛡️') return <Shield className="w-6 h-6 text-[#00F0FF]" />;
    return <span className="text-2xl">{icon}</span>;
  };

  return (
    <header className="relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#050505]" />
      <div className="absolute inset-0 grid-bg opacity-10" />
      
      {/* Animated gradient accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent opacity-60" />
      
      {/* Top Navigation Bar */}
      <div className="relative z-10 border-b border-white/5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Left: Back & Tool Info */}
            <div className="flex items-center space-x-4">
              <Link 
                to={backLink} 
                className="group flex items-center gap-2 px-3 py-2 bg-[#1A1A1A]/50 border border-white/5 hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/5 transition-all"
              >
                <ChevronLeft className="w-4 h-4 text-[#888] group-hover:text-[#00F0FF] transition-colors" />
                <span className="text-sm text-[#888] group-hover:text-[#00F0FF] font-medium tracking-wide transition-colors">Back</span>
              </Link>
              
              <div className="hidden sm:flex items-center space-x-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 flex items-center justify-center">
                    {getIcon()}
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-[#00F0FF]/20 blur-xl -z-10" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-bold uppercase tracking-wider text-white">{title}</h1>
                  <p className="text-xs text-[#00F0FF]/70">{subtitle}</p>
                </div>
              </div>
            </div>
            
            {/* Right: Progress Steps - Enhanced Design */}
            {steps && steps.length > 0 && (
              <div className="flex items-center">
                <div className="hidden md:flex items-center bg-[#1A1A1A]/30 border border-white/5 p-2">
                  {steps.map((step, idx) => (
                    <div key={step.num} className="flex items-center">
                      <div className="relative group">
                        <div className={`
                          w-10 h-10 flex items-center justify-center text-sm font-bold transition-all duration-300
                          ${currentStep === step.num 
                            ? 'bg-[#00F0FF] text-black shadow-[0_0_20px_rgba(0,240,255,0.4)]' 
                            : currentStep > step.num
                              ? 'bg-[#00F0FF]/20 border border-[#00F0FF]/50 text-[#00F0FF]'
                              : 'bg-[#1A1A1A] border border-white/10 text-[#666]'
                          }
                        `}>
                          {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                        </div>
                        {/* Step label tooltip */}
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs text-[#888] whitespace-nowrap">{step.label}</span>
                        </div>
                      </div>
                      {idx < steps.length - 1 && (
                        <div className={`w-8 h-0.5 mx-1 transition-all duration-300 ${
                          currentStep > step.num 
                            ? 'bg-gradient-to-r from-[#00F0FF] to-[#00F0FF]/50' 
                            : 'bg-white/10'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Mobile step indicator */}
                <div className="md:hidden flex items-center gap-2 bg-[#1A1A1A]/50 border border-white/10 px-3 py-2">
                  <span className="text-[#00F0FF] font-bold">{currentStep}</span>
                  <span className="text-[#666]">/</span>
                  <span className="text-[#888]">{steps.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Hero Section with Step Title */}
      <div className="relative py-12 md:py-16 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 hero-glow opacity-20" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00F0FF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#FF003C]/5 rounded-full blur-3xl" />
        
        {/* Diagonal lines decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 border border-[#00F0FF]/10 rotate-45" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 border border-[#00F0FF]/5 rotate-45" />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          {/* Mobile tool icon */}
          <div className="sm:hidden flex justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 flex items-center justify-center">
                {getIcon()}
              </div>
              <div className="absolute inset-0 bg-[#00F0FF]/20 blur-xl -z-10" />
            </div>
          </div>
          
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-3">
            {currentStepTitle}
          </h2>
          <p className="text-[#888] text-sm md:text-base max-w-xl mx-auto">{currentStepSubtitle}</p>
          
          {/* Step progress dots for mobile */}
          {steps && steps.length > 0 && (
            <div className="md:hidden flex justify-center gap-2 mt-6">
              {steps.map((step) => (
                <div 
                  key={step.num}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentStep === step.num 
                      ? 'bg-[#00F0FF] w-6' 
                      : currentStep > step.num
                        ? 'bg-[#00F0FF]/50'
                        : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default StepHeader;
